import { type CSSProperties } from 'react';

import pack10Png from '/img/shop/v2/tgem/pack-10.png';
import pack10Webp from '/img/shop/v2/tgem/pack-10.webp';
import pack200Png from '/img/shop/v2/tgem/pack-200.png';
import pack200Webp from '/img/shop/v2/tgem/pack-200.webp';
import pack500Png from '/img/shop/v2/tgem/pack-500.png';
import pack500Webp from '/img/shop/v2/tgem/pack-500.webp';
import pack2000Png from '/img/shop/v2/tgem/pack-2000.png';
import pack2000Webp from '/img/shop/v2/tgem/pack-2000.webp';
import pack5000Png from '/img/shop/v2/tgem/pack-5000.png';
import pack5000Webp from '/img/shop/v2/tgem/pack-5000.webp';
import pack10000Png from '/img/shop/v2/tgem/pack-10000.png';
import pack10000Webp from '/img/shop/v2/tgem/pack-10000.webp';

/**
 * VIP days granted with a T-Gem pack — the backend's vipDurationAmountMap
 * (config/configuration.ts), keep in sync.
 */
const tgemPackVipDays: Record<number, number> = {
  220: 1,
  575: 3,
  2500: 14,
  6500: 30,
};

/** How a pack is shown: base amount + free bonus (105 → 100 +5). */
const tgemPackBonus: Record<number, { base: number; bonus: number }> = {
  105: { base: 100, bonus: 5 },
  220: { base: 200, bonus: 20 },
  575: { base: 500, bonus: 75 },
  2500: { base: 2000, bonus: 500 },
  6500: { base: 5000, bonus: 1500 },
  13000: { base: 10000, bonus: 3000 },
};

interface PackArt {
  png: string;
  webp: string;
  /** Figma crop of the art inside its 136px box */
  style: CSSProperties;
}

const art = (
  png: string,
  webp: string,
  [left, top, width, height]: [number, number, number, number],
): PackArt => ({
  png,
  webp,
  style: {
    left: `${left}%`,
    top: `${top}%`,
    width: `${width}%`,
    height: `${height}%`,
  },
});

// Pack art by base amount, the smallest packs share the small stack
const tgemPackArt: Record<number, PackArt> = {
  10: art(pack10Png, pack10Webp, [0, -7.53, 100, 128.36]),
  100: art(pack10Png, pack10Webp, [0, -7.53, 100, 128.36]),
  200: art(pack200Png, pack200Webp, [0, 0, 100, 100]),
  500: art(pack500Png, pack500Webp, [-4.79, -2.27, 107.9, 107.9]),
  2000: art(pack2000Png, pack2000Webp, [-11.54, -14.42, 123.08, 123.08]),
  5000: art(pack5000Png, pack5000Webp, [-0.79, -1.59, 101.59, 101.59]),
  10000: art(pack10000Png, pack10000Webp, [-11.87, -13.8, 123.74, 123.74]),
};

const packArtFor = (base: number): PackArt => {
  const sizes = Object.keys(tgemPackArt)
    .map(Number)
    .filter((size) => size <= base);

  return tgemPackArt[sizes.length ? Math.max(...sizes) : 10];
};

const packNames: Record<number, string> = {
  10: 'Small Pack',
};

export { packArtFor, packNames, tgemPackBonus, tgemPackVipDays };
